import React, { useState, useEffect, useCallback, memo } from "react";
import { useAuth0 } from "../../react-auth0-spa";
import notify from "devextreme/ui/notify";
import { PlannedRunDetails, defaultSpatialManifest, newRun } from "./constants";
import { LoadPanel } from "devextreme-react/load-panel";
import { Button, Tabs, ValidationSummary } from "devextreme-react";
import AddRunSequencingContent from "./AddRunSequencingContent";
import AddRunSpatialContent from "./AddRunSpatialContent";

const preferredSpatialBranch =
  "v27x-101124-Driftv2AddCP01C002-S2Img-ImprovedPostWash-ImprovedPriming-BridgingStudy";

export interface AddRunFormProps {
  visible: boolean;
  selectedRun: PlannedRunDetails;
  isEditing: boolean;
  onSubmit: (
    data: PlannedRunDetails,
    editedData: Partial<PlannedRunDetails>
  ) => Promise<boolean>;
  onCancel: () => void;
}

export interface LookupsType {
  groups: any[];
  tags: any[];
  tileSelectionModules: any[];
  emails: any[];
  projects: any[];
}

interface CartridgePartNumber {
  ID: string;
  Name: string;
  PartNumber: string;
  CompatiblePartNumbers: string[];
  Description: string;
  SpatialKitType: number[];
}

const AddRunForm = memo((props: AddRunFormProps) => {
  const { loading, fetchWithCheck } = useAuth0();
  const [lookups, setLookups] = useState<LookupsType>({
    groups: [],
    tags: [],
    tileSelectionModules: [],
    emails: [],
    projects: [],
  });
  const [data, setData] = useState<PlannedRunDetails>(props.selectedRun);
  const [editedData, setEditedData] = useState<Partial<PlannedRunDetails>>({});
  const [forceRefresh, setForceRefresh] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [cartridges, setCartridges] = useState<CartridgePartNumber[]>([]);

  console.log("selected run", props.selectedRun);

  useEffect(() => {
    fetchWithCheck("/partNumbers").then((vals) => {
      setCartridges(vals[4]);
    });
  }, [loading, fetchWithCheck, data?.spatialKitType]);

  useEffect(() => {
    fetchWithCheck("/groups").then((groups) =>
      setLookups((v) => ({ ...v, groups }))
    );
  }, [loading, fetchWithCheck]);

  useEffect(() => {
    fetchWithCheck("/emails").then((emails) =>
      setLookups((v) => ({ ...v, emails }))
    );
  }, [loading, fetchWithCheck]);

  useEffect(() => {
    fetchWithCheck("/projects").then((projects) =>
      setLookups((v) => ({ ...v, projects }))
    );
  }, [loading, fetchWithCheck]);

  useEffect(() => {
    fetchWithCheck("/tags").then((tags) => setLookups((v) => ({ ...v, tags })));
  }, [loading, fetchWithCheck]);

  useEffect(() => {
    fetchWithCheck("/tileSelectionModules").then((tsm) => {
      const tileSelectionModules = [];
      let found = !props.isEditing;
      for (let i = 0; i < tsm.length; i++) {
        if (
          props.isEditing &&
          props.selectedRun &&
          props.selectedRun.tileSelectionModule ===
            `tile_selections/${tsm[i]}.txt`
        )
          found = true;

        tileSelectionModules.push({
          name: tsm[i],
          value: `tile_selections/${tsm[i]}.txt`,
        });
      }

      if (!found)
        tileSelectionModules.push({
          value: props.selectedRun.tileSelectionModule,
          name: props.selectedRun.tileSelectionModule
            .replaceAll("tile_selections/", "")
            .replaceAll(".txt", ""),
        });

      setLookups((v) => ({ ...v, tileSelectionModules }));
    });
  }, [loading, fetchWithCheck, props.isEditing, props.selectedRun]);

  useEffect(() => {
    if (!(window as any).recipeBranches || forceRefresh) {
      setFetching(true);
      setForceRefresh(false);
      fetchWithCheck("/recipeBranches")
        .then((recipeBranches) => {
          (window as any).recipeBranches = recipeBranches;
        })
        .finally(() => setFetching(false));
    } else {
      setLookups((v) => ({
        ...v,
        recipeBranches: (window as any).recipeBranches,
      }));
    }
  }, [loading, fetchWithCheck, forceRefresh]);

  const handleSubmit = useCallback(
    (e) => {
      e.preventDefault();
      console.log("edited data", editedData);

      if (
        "panelFilePath" in editedData &&
        editedData["panelFilePath"] == null
      ) {
        // if the panelFilePath was edited but set to a null value, set it to an empty string instead
        // otherwise the value will not be updated during a patch
        editedData["panelFilePath"] = "";
      }

      // set batch info and spatialPanelJson to empty if a panelFilePath is set
      if (data.panelFilePath) {
        data.batchInfo = [];
        data.spatialPanelJson = "";
        data.spatialPanel = "";
      }
      if (editedData["panelFilePath"]) {
        editedData["batchInfo"] = [];
        editedData["spatialPanelJson"] = "";
        editedData["spatialPanel"] = "";
      }

      if (
        !editedData["spatialPanelJson"] &&
        editedData["spatialPanel"] &&
        data.spatialPanelJson
      ) {
        editedData["spatialPanelJson"] = data.spatialPanelJson;
      }

      // Tile overlap and post-run H&E only apply to tissue sections. Normalize here
      // rather than when the kit type changes, so a value typed for KitTypeTissue
      // survives switching kit types and switching back. The inputs are hidden for
      // other kit types (see isTissueKit in AddRunSpatialContent).
      if (data.spatialKitType !== "KitTypeTissue") {
        data.tileOverlapX = 0;
        data.tileOverlapY = 0;
        data.saveFlowCellForPostRunHE = false;
        data.spatialVcf = "";
        editedData["tileOverlapX"] = 0;
        editedData["tileOverlapY"] = 0;
        editedData["saveFlowCellForPostRunHE"] = false;
        editedData["spatialVcf"] = "";
      }

      if (!props.onSubmit(data, editedData)) {
        notify(
          {
            message: "Failed to create run",
            position: {
              my: "center top",
              at: "center top",
            },
          },
          "error",
          3000
        );
      }
    },
    [props, data, editedData]
  );

  useEffect(() => {
    setData({
      ...props.selectedRun,
      spatialPanel: props.selectedRun.spatialPanelJson,
      spatialPanelType: props.selectedRun.spatialPanelJson ? "JSON" : "CSV",
    });
    setEditedData({
      batchInfo: props.selectedRun
        ? props.selectedRun.batchInfo
        : newRun.batchInfo,
      tileOverlapX: props.selectedRun?.tileOverlapX ?? 0,
      tileOverlapY: props.selectedRun?.tileOverlapY ?? 0,
    });
  }, [props.selectedRun]);

  console.log("mydata", data);
  console.log("editeddata", editedData);

  return (
    <div id="myform" style={{ height: "100%", width: "100%" }}>
      <Tabs
        dataSource={["Sequencing", "Spatial"]}
        selectedItem={data?.runType}
        onSelectedItemChange={(e) => {
          const analysisSettings =
            e === "Sequencing"
              ? {
                  analysisToolRep:
                    "registry.gitlab.com/elembio/analysis/primaryanalysisdotnetcore",
                  imageName:
                    "registry.gitlab.com/elembio/analysis/primaryanalysisdotnetcore:3",
                  arguments:
                    "bash /primaryanalysisdotnetcore/run_container_analysis.sh",
                }
              : {
                  analysisToolRep:
                    "registry.gitlab.com/elembio/analysis/primaryanalysisdotnetcore",
                  imageName:
                    "registry.gitlab.com/elembio/analysis/primaryanalysisdotnetcore:3",
                  arguments:
                    "bash /primaryanalysisdotnetcore/run_container_analysis.sh -ASSAYMODE=3D -RUNMANIFEST=RunManifest.json",
                };
          const manifestSetings =
            !props.isEditing &&
            data.manifest === newRun.manifest &&
            editedData["manifest"] === undefined
              ? {
                  manifest: defaultSpatialManifest,
                  recipeBranch: preferredSpatialBranch,
                }
              : {};

          const saveIntensitiesSettings =
            !props.isEditing && e === "Spatial"
              ? { saveIntensities: "all" }
              : {};

          setData((v) => {
            return {
              ...v,
              runType: e,
              ...analysisSettings,
              ...manifestSetings,
              ...saveIntensitiesSettings,
            };
          });
          setEditedData((v) => {
            return {
              ...v,
              runType: e,
              ...analysisSettings,
              ...manifestSetings,
              ...saveIntensitiesSettings,
            };
          });
        }}
        height={40}
      />
      {data?.runType === "Sequencing" && (
        <form
          style={{
            width: "100%",
            height: "calc(100% - 40px)",
          }}
          onSubmit={handleSubmit}
        >
          <AddRunSequencingContent
            lookups={lookups}
            data={data}
            setData={setData}
            setEditedData={setEditedData}
            setForceRefresh={setForceRefresh}
            nameIsReadOnly={props.isEditing && props.selectedRun?.groupID !== 0}
          />

          <ValidationSummary />
          <Button
            width="120px"
            id="button"
            text="Save"
            type="default"
            style={{ marginTop: "20px" }}
            useSubmitBehavior={true}
          />

          <Button
            width="120px"
            id="button"
            text="Cancel"
            style={{ marginTop: "20px", marginLeft: "20px" }}
            onClick={() => {
              props.onCancel();
            }}
            type="normal"
          />
        </form>
      )}

      {data?.runType === "Spatial" && (
        <form
          style={{
            width: "100%",
            height: "calc(100% - 40px)",
          }}
          onSubmit={handleSubmit}
        >
          <AddRunSpatialContent
            lookups={lookups}
            data={data}
            setData={setData}
            setEditedData={setEditedData}
            setForceRefresh={setForceRefresh}
            nameIsReadOnly={props.isEditing && props.selectedRun?.groupID !== 0}
            cartridges={cartridges}
          />

          <ValidationSummary />
          <Button
            width="120px"
            id="button"
            text="Save"
            type="default"
            style={{ marginTop: "20px" }}
            useSubmitBehavior={true}
          />

          <Button
            width="120px"
            id="button"
            text="Cancel"
            style={{ marginTop: "20px", marginLeft: "20px" }}
            onClick={() => {
              props.onCancel();
            }}
            type="normal"
          />
        </form>
      )}

      <LoadPanel visible={fetching} showIndicator={true} showPane={true} />
    </div>
  );
});

export default AddRunForm;
